import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Building2, ChevronLeft } from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useSetupChecklist, type ChecklistSectionKey } from '../../hooks/useSetupChecklist';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { CompanyProfileStep, type CompanyProfileData } from './steps/CompanyProfileStep';
import { MoveTypesStep, type MoveTypesData } from './steps/MoveTypesStep';
import { FleetStep } from './steps/FleetStep';
import { ResidentialSettingsStep, type ResidentialSettingsData } from './steps/ResidentialSettingsStep';
import { CommercialSettingsStep, type CommercialSettingsData } from './steps/CommercialSettingsStep';
import { MaterialsPricingStep, type MaterialsPricingData } from './steps/MaterialsPricingStep';
import { PaymentsStep, type PaymentsData } from './steps/PaymentsStep';
import { OnlineLeadCaptureStep, type OnlineLeadCaptureData } from './steps/OnlineLeadCaptureStep';
import { InviteTeamStep } from './steps/InviteTeamStep';
import { api } from '../../lib/api';
import { SETUP_SECTION_REMAINING, flatRateTiersMessage, missingRatesMessage, missingRequiredRates } from '@iomi/shared';

/** Where the "Almost there" recap sends the admin to finish each section. */
const SECTION_LINKS: Record<ChecklistSectionKey, { label: string; to: string }> = {
  companyProfileCompleted: { label: 'Company Profile', to: '/admin/company/profile' },
  moveTypesCompleted: { label: 'Features', to: '/admin/platform/features' },
  fleetCompleted: { label: 'Manage Warehouses', to: '/admin/operations/manage-warehouses' },
  residentialSettingsCompleted: { label: 'Rate Cards', to: '/admin/quoting/rate-cards' },
  commercialSettingsCompleted: { label: 'Rate Cards', to: '/admin/quoting/rate-cards' },
  paymentsCompleted: { label: 'Payments', to: '/admin/payments' },
  materialsPricingCompleted: { label: 'Inventory Items', to: '/admin/quoting/inventory-items' },
};

/** Result of saving a step: ok, or a message that blocks Continue. */
type StepSaveResult = { ok: true } | { ok: false; message: string };
const OK: StepSaveResult = { ok: true };
const blocked = (message: string): StepSaveResult => ({ ok: false, message });

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Steps whose fields are saved only on Continue, so Skip discards what was typed. */
const STEPS_WITH_UNSAVED_INPUT = new Set([
  'company-profile', 'move-types', 'residential-settings', 'commercial-settings', 'materials-pricing', 'payments',
  'online-lead-capture',
]);

// ── Step definition ────────────────────────────────────────────────────────

export interface WizardStepConfig {
  id: string;
  title: string;
  subtitle: string;
  checklistKey?: ChecklistSectionKey;
}

const ALL_STEPS: WizardStepConfig[] = [
  {
    id: 'company-profile',
    title: 'Company Profile',
    subtitle: 'Your customer-facing identity for quotes and documents',
    checklistKey: 'companyProfileCompleted',
  },
  {
    id: 'move-types',
    title: 'Move Types',
    subtitle: 'What types of moves does your company handle?',
    checklistKey: 'moveTypesCompleted',
  },
  {
    id: 'fleet',
    title: 'Warehouse & Trucks',
    subtitle: 'Add your warehouse location and trucks',
    checklistKey: 'fleetCompleted',
  },
  {
    id: 'residential-settings',
    title: 'Residential Settings',
    subtitle: 'Configure billing, rates, and quote defaults for residential moves',
    checklistKey: 'residentialSettingsCompleted',
  },
  {
    id: 'commercial-settings',
    title: 'Commercial Settings',
    subtitle: 'Configure rates and quote defaults for commercial moves',
    checklistKey: 'commercialSettingsCompleted',
  },
  {
    id: 'materials-pricing',
    title: 'Materials Pricing',
    subtitle: 'Set what you charge for crates, cartons, carts and supplies',
    checklistKey: 'materialsPricingCompleted',
  },
  {
    id: 'payments',
    title: 'Payments',
    subtitle: 'Set up how you collect payments from customers',
    checklistKey: 'paymentsCompleted',
  },
  {
    id: 'online-lead-capture',
    title: 'Online Lead Capture',
    subtitle: 'Enable your public booking form to capture leads online',
  },
  {
    id: 'invite-team',
    title: 'Invite Your Team',
    subtitle: 'Add estimators, movers, and dispatchers',
  },
  {
    id: 'done',
    title: 'All Done!',
    subtitle: 'Your organization is ready to go',
  },
];

// ── Main page ──────────────────────────────────────────────────────────────

/** Steps `/setup?step=` can open on (the new-estimate pricing warning links here). */
const REQUESTED_STEP_IDS: Record<string, string[]> = {
  rates: ['residential-settings', 'commercial-settings'],
  'materials-pricing': ['materials-pricing'],
};

export function SetupWizardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedStep = REQUESTED_STEP_IDS[searchParams.get('step') ?? ''] ? searchParams.get('step') : null;
  const { activeOrg, adminOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? '';
  const { org, loading: orgLoading, refetch: refetchOrg } = useOrgSettings(orgId);
  const { checklist, loading: checklistLoading, updateSection, complete } = useSetupChecklist(orgId);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  // Why Continue was blocked on the current step (shown above the footer buttons).
  const [stepError, setStepError] = useState<string | null>(null);

  // Step data refs — each step component writes its data here via onChange
  const companyProfileData = useRef<CompanyProfileData | null>(null);
  const moveTypesData = useRef<MoveTypesData | null>(null);
  const residentialSettingsData = useRef<ResidentialSettingsData | null>(null);
  const commercialSettingsData = useRef<CommercialSettingsData | null>(null);
  const materialsPricingData = useRef<MaterialsPricingData | null>(null);
  const paymentsData = useRef<PaymentsData | null>(null);
  const onlineLeadCaptureData = useRef<OnlineLeadCaptureData | null>(null);
  // An Add Warehouse / Add Truck form left open on the Fleet step (not saved until Add is pressed).
  const fleetOpenForm = useRef<string | null>(null);
  const [skipConfirm, setSkipConfirm] = useState<{ message: string } | null>(null);

  // Redirect non-admins
  useEffect(() => {
    if (!adminOrg && activeOrg) {
      navigate('/', { replace: true });
    }
  }, [adminOrg, activeOrg, navigate]);

  // Compute visible steps based on org feature flags
  const visibleSteps = useMemo(() => {
    if (!org) return [];

    return ALL_STEPS.filter((step) => {
      switch (step.id) {
        case 'move-types':
          // Only show if plan allows BOTH commercial and residential
          return org.allowedFeatureCommercialMovingQuotes === true
            && org.allowedFeatureResidentialMovingQuotes === true;
        case 'residential-settings':
          return org.featureResidentialMovingQuotes === true;
        case 'commercial-settings':
          return org.featureCommercialMovingQuotes === true;
        case 'materials-pricing':
          return org.featureCommercialMovingQuotes === true || org.featureResidentialMovingQuotes === true;
        case 'payments':
          return org.allowedFeatureCustomerPayments === true
            || org.allowedFeatureStripeConnect === true;
        case 'online-lead-capture':
          return org.allowedFeatureOnlineLeadCapture === true;
        default:
          return true; // company-profile, fleet, invite-team, done are always shown
      }
    });
  }, [org]);

  // On load, jump to the requested step (?step=), else the first incomplete one
  useEffect(() => {
    if (!checklist || visibleSteps.length === 0) return;
    if (requestedStep) {
      const target = visibleSteps.findIndex((s) => REQUESTED_STEP_IDS[requestedStep].includes(s.id));
      if (target >= 0) { setCurrentStepIndex(target); return; }
    }
    const firstIncomplete = visibleSteps.findIndex(
      (s) => s.checklistKey && !checklist[s.checklistKey],
    );
    if (firstIncomplete > 0) {
      setCurrentStepIndex(firstIncomplete);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklist !== null, visibleSteps.length]);

  // A blocking message belongs to the step it was raised on.
  useEffect(() => { setStepError(null); }, [currentStepIndex]);

  // Decided once, when the wizard opens: an org that already finished setup
  // goes home. Finishing the last section mid-session stamps completedAt too,
  // but the admin stays to see Lead Capture, Invite Team and the Done screen.
  const [openedComplete, setOpenedComplete] = useState<boolean | null>(null);
  useEffect(() => {
    if (openedComplete !== null || checklistLoading) return;
    // A link to a specific step (?step=) opens it even when setup was finished.
    setOpenedComplete(!requestedStep && !!checklist?.isComplete && !!checklist.completedAt);
  }, [checklist, checklistLoading, openedComplete, requestedStep]);
  useEffect(() => {
    if (openedComplete) navigate('/', { replace: true });
  }, [openedComplete, navigate]);

  const currentStep = visibleSteps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;
  const isDoneStep = currentStep?.id === 'done';

  // Count content steps (exclude "done")
  const contentSteps = visibleSteps.filter((s) => s.id !== 'done');
  const progressIndex = Math.min(currentStepIndex, contentSteps.length);
  const progressPercent = contentSteps.length > 0
    ? Math.round((progressIndex / contentSteps.length) * 100)
    : 0;

  /** Leave the wizard: mark it finished, then go home or straight into a new quote. */
  const finishWizard = useCallback(async (destination: 'home' | 'new-quote') => {
    await complete();
    navigate(destination === 'new-quote' ? '/?new=1' : '/', { replace: true });
  }, [complete, navigate]);

  const goNext = useCallback(async () => {
    if (isLastStep) {
      await finishWizard('home');
      return;
    }
    setCurrentStepIndex((i) => Math.min(i + 1, visibleSteps.length - 1));
  }, [isLastStep, finishWizard, visibleSteps.length]);

  const goBack = useCallback(() => {
    setStepError(null);
    setCurrentStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const saveCurrentStep = useCallback(async (): Promise<StepSaveResult> => {
    if (!currentStep) return OK;

    switch (currentStep.id) {
      case 'company-profile': {
        const data = companyProfileData.current;
        if (!data) return OK;
        if (!data.legalName.trim()) return blocked('Enter your legal company name to continue.');
        if (!data.billingEmail.trim()) return blocked('Enter your business email to continue.');
        if (!EMAIL_RE.test(data.billingEmail.trim())) {
          return blocked('Enter a valid business email address (for example info@yourcompany.com).');
        }
        await api.patch(`/org-admin/${orgId}/settings`, {
          name: data.displayName.trim() || data.legalName.trim(),
          legalName: data.legalName.trim(),
          billingEmail: data.billingEmail.trim(),
          phone: data.phone.trim() || undefined,
          website: data.website.trim() || undefined,
          businessAddress: data.businessAddress ?? undefined,
          logoBase64: data.logoBase64 ?? '',
          bookingConfig: { brandPrimaryColor: data.brandPrimaryColor },
          quoteFooter: data.quoteFooter.trim(),
        });
        return OK;
      }
      case 'move-types': {
        const data = moveTypesData.current;
        if (!data) return OK;
        if (!data.commercialEnabled && !data.residentialEnabled) {
          return blocked('Enable at least one move type to continue.');
        }
        await api.patch(`/org-admin/${orgId}/settings`, {
          featureCommercialMovingQuotes: data.commercialEnabled,
          featureResidentialMovingQuotes: data.residentialEnabled,
        });
        // Refetch org settings so visibleSteps recomputes
        if (refetchOrg) await refetchOrg();
        return OK;
      }
      case 'residential-settings': {
        const data = residentialSettingsData.current;
        if (!data) return OK;
        if (data.openEditor) {
          return blocked(`Save or cancel the open ${data.openEditor} edit before continuing, or its changes will be lost.`);
        }
        // The rates step only counts as done with real prices: hourly needs
        // every rate the formula uses above $0; flat rate needs every enabled
        // bedroom tier priced.
        if (data.pricingModel === 'hourly_rate') {
          const message = missingRatesMessage(missingRequiredRates(data.rates, data.useBlendedTruckDriverRate));
          if (message) return blocked(message);
        } else {
          if (!data.flatRates) return blocked('Flat rates are still loading. Try again in a moment.');
          const message = flatRateTiersMessage(data.flatRates.rooms);
          if (message) return blocked(message);
        }
        await api.patch(`/org-admin/${orgId}/settings`, {
          residentialPricingModel: data.pricingModel,
          useBlendedTruckDriverRate: data.useBlendedTruckDriverRate,
          residentialDefaultTravelTimeRate: data.travelTimeRate,
          residentialFixedTravelTimeHours: data.fixedTravelTimeHours,
          residentialDefaultQuoteValidityDays: data.quoteValidityDays,
          localMoveRadiusType: data.localMoveRadiusType,
          localMoveRadiusValue: data.localMoveRadiusValue,
          residentialShiftSettings: data.shiftSettings,
          residentialItemizationPreference: data.itemizationPreference,
        });
        if (data.pricingModel === 'hourly_rate') {
          for (const [slug, rate] of Object.entries(data.rates)) {
            await api.patch(`/org-admin/${orgId}/rates/${slug}`, { hourlyRate: rate });
          }
        } else if (data.flatRates) {
          await api.put(`/org-admin/${orgId}/residential-flat-rates`, data.flatRates);
        }
        // The Commercial Settings step reads the saved pricing model.
        if (refetchOrg) await refetchOrg();
        return OK;
      }
      case 'commercial-settings': {
        const data = commercialSettingsData.current;
        if (!data) return OK;
        if (data.openEditor) {
          return blocked(`Save or cancel the open ${data.openEditor} edit before continuing, or its changes will be lost.`);
        }
        const message = missingRatesMessage(missingRequiredRates(data.rates, data.useBlendedTruckDriverRate));
        if (message) return blocked(message);
        await api.patch(`/org-admin/${orgId}/settings`, {
          useBlendedTruckDriverRate: data.useBlendedTruckDriverRate,
          commercialDefaultTravelTimeRate: data.travelTimeRate,
          commercialFixedTravelTimeHours: data.fixedTravelTimeHours,
          commercialEfficiencyMultiplier: data.efficiencyMultiplier,
          commercialDefaultQuoteValidityDays: data.quoteValidityDays,
          localMoveRadiusType: data.localMoveRadiusType,
          localMoveRadiusValue: data.localMoveRadiusValue,
          commercialShiftSettings: data.shiftSettings,
          commercialItemizationPreference: data.itemizationPreference,
        });
        for (const [slug, rate] of Object.entries(data.rates)) {
          await api.patch(`/org-admin/${orgId}/rates/${slug}`, { hourlyRate: rate });
        }
        return OK;
      }
      case 'materials-pricing': {
        const data = materialsPricingData.current;
        if (!data || !data.loaded) {
          return blocked('Your materials haven’t loaded yet. Wait a moment, or tap Skip for Now and set prices later under Inventory Items.');
        }
        if (data.invalidFields.length > 0) {
          return blocked(`Fix ${data.invalidFields[0]}${data.invalidFields.length > 1 ? ` and ${data.invalidFields.length - 1} more` : ''} to continue.`);
        }
        if (data.rentalWithoutCost.length > 0) {
          const [first] = data.rentalWithoutCost;
          const more = data.rentalWithoutCost.length - 1;
          return blocked(`${first}${more > 0 ? ` and ${more} more` : ''} ${more > 0 ? 'have' : 'has'} a rental length but no unit cost. Add a cost or clear the rental length.`);
        }
        // A few at a time: a full catalog edit can be dozens of items.
        for (let i = 0; i < data.changes.length; i += 8) {
          await Promise.all(data.changes.slice(i, i + 8).map(({ id, unitCost, rentalDays }) =>
            api.patch(`/org-admin/${orgId}/inventory/${id}`, { unitCost, rentalDays })));
        }
        return OK;
      }
      case 'payments': {
        const data = paymentsData.current;
        if (!data) return OK;
        await api.patch(`/org-admin/${orgId}/settings`, {
          featureCustomerPayments: data.enablePayments,
          calculateTaxes: data.taxMode !== 'none',
          defaultTaxRate: data.taxMode === 'manual' ? data.defaultTaxRate : 0,
          paymentScheduleDefaults: {
            collectDeposit: data.collectDeposit,
            depositType: data.depositType,
            depositAmount: data.depositAmount,
            balanceCollection: data.balanceCollection,
            // "Split before & after" is meaningless without its percentage.
            splitBeforeMovePercent: data.balanceCollection === 'split' ? (data.splitBeforeMovePercent ?? 50) : undefined,
          },
        });
        return OK;
      }
      case 'online-lead-capture': {
        const data = onlineLeadCaptureData.current;
        if (!data) return OK;
        if (data.openEditor) {
          return blocked(`Save or cancel the open ${data.openEditor} edit before continuing, or its changes will be lost.`);
        }
        if (data.enabled) {
          if (!data.slug) return blocked('Enter a booking URL slug to continue.');
          // The slug is the public address: if it is taken or invalid, say so
          // and stay on the step instead of showing a link that was never saved.
          try {
            await api.patch(`/org-admin/${orgId}/slug`, { slug: data.slug });
          } catch (err) {
            return blocked(err instanceof Error ? `Booking URL not saved: ${err.message}` : 'Booking URL could not be saved.');
          }
        }
        await api.patch(`/org-admin/${orgId}/settings`, {
          featureOnlineLeadCapture: data.enabled,
        });
        if (data.enabled) {
          await api.patch(`/org-admin/${orgId}/booking-config`, {
            enabled: true,
            welcomeMessage: data.welcomeMessage,
            enableLockInDate: data.enableLockInDate,
            enableRequestEstimate: data.enableRequestEstimate,
            enableShowPriceEstimate: data.enableShowPriceEstimate,
          });
        }
        return OK;
      }
      case 'fleet': {
        if (fleetOpenForm.current) {
          return blocked(`Finish the ${fleetOpenForm.current} form (press Add) or cancel it before continuing. It isn't saved yet.`);
        }
        // Auto-assign trucks to the single warehouse if only one exists
        try {
          const [whRes, trRes] = await Promise.all([
            api.get<{ success: boolean; data: { id: string }[] }>(`/org-admin/${orgId}/warehouses`),
            api.get<{ success: boolean; data: { id: string; currentLocation?: string }[] }>(`/org-admin/${orgId}/trucks`),
          ]);
          const warehouses = whRes.data;
          const trucks = trRes.data;
          if (warehouses.length === 1 && trucks.length > 0) {
            const warehouseId = warehouses[0].id;
            await Promise.all(
              trucks
                .filter(t => !t.currentLocation)
                .map(t => api.patch(`/org-admin/${orgId}/trucks/${t.id}`, { currentLocation: warehouseId })),
            );
          }
        } catch {
          // Non-critical — trucks will just not have a default location
        }
        return OK;
      }
      default:
        return OK;
    }
  }, [currentStep, orgId, refetchOrg]);

  const handleContinue = useCallback(async () => {
    setSaving(true);
    setStepError(null);
    try {
      let result: StepSaveResult;
      try {
        result = await saveCurrentStep();
      } catch (err) {
        result = blocked(err instanceof Error ? err.message : 'Could not save this step. Please try again.');
      }
      if (!result.ok) { setStepError(result.message); return; }
      // Mark checklist section as complete if applicable
      if (currentStep?.checklistKey) {
        await updateSection({ [currentStep.checklistKey]: true });
      }
      await goNext();
    } finally {
      setSaving(false);
    }
  }, [currentStep, updateSection, goNext, saveCurrentStep]);

  /** Skip is explicit about what it discards: a confirm on steps with unsaved input. */
  const handleSkip = useCallback(() => {
    setStepError(null);
    const stepId = currentStep?.id ?? '';
    if (stepId === 'fleet' && fleetOpenForm.current) {
      setSkipConfirm({ message: `The ${fleetOpenForm.current} form you started hasn't been added. Skip this step anyway? It won't be saved.` });
      return;
    }
    if (STEPS_WITH_UNSAVED_INPUT.has(stepId)) {
      setSkipConfirm({ message: 'Nothing you entered on this step will be saved. You can finish it later in the Admin Panel, and the setup banner will remind you.' });
      return;
    }
    void goNext();
  }, [currentStep, goNext]);

  const confirmSkip = useCallback(() => {
    setSkipConfirm(null);
    void goNext();
  }, [goNext]);

  // Loading state
  if (orgLoading || checklistLoading || visibleSteps.length === 0 || openedComplete !== false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* ── Header — matches the app's TopBar (bg-plum) ────────────────── */}
      <header
        className="sticky top-0 z-10 bg-plum px-4"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 h-14 mt-1">
            {!isFirstStep && !isDoneStep && (
              <button
                onClick={goBack}
                className="p-1.5 -ml-1.5 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Go back"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
            )}
            <div className="flex items-center gap-2 min-w-0">
              <Building2 className="w-5 h-5 text-white/70 shrink-0" />
              <span className="text-sm font-semibold text-white truncate">
                Set Up Your Moving Company
              </span>
            </div>
            {!isDoneStep && (
              <span className="ml-auto text-xs text-white/60 shrink-0">
                Step {progressIndex + 1} of {contentSteps.length}
              </span>
            )}
          </div>

          {/* Progress bar */}
          {!isDoneStep && (
            <div className="h-1 bg-white/20 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-white rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          )}
        </div>
      </header>

      {/* ── Step content ───────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto px-4 py-6 pb-24">
        <div className="max-w-2xl mx-auto">
          {!isDoneStep && (
            <div className="mb-6">
              <h1 className="font-display text-h1 text-midnight">
                {currentStep?.title}
              </h1>
              <p className="mt-1 text-small text-slate">
                {currentStep?.subtitle}
              </p>
            </div>
          )}

          {/* Step content rendered by individual step components */}
          <SetupStepContent
            stepId={currentStep?.id ?? ''}
            orgId={orgId}
            org={org}
            checklist={checklist}
            visibleSteps={visibleSteps}
            showErrors={stepError !== null}
            onFinish={finishWizard}
            onCompanyProfileChange={(data) => { companyProfileData.current = data; }}
            onMoveTypesChange={(data) => { moveTypesData.current = data; }}
            onResidentialSettingsChange={(data) => { residentialSettingsData.current = data; }}
            onCommercialSettingsChange={(data) => { commercialSettingsData.current = data; }}
            onMaterialsPricingChange={(data) => { materialsPricingData.current = data; }}
            onPaymentsChange={(data) => { paymentsData.current = data; }}
            onOnlineLeadCaptureChange={(data) => { onlineLeadCaptureData.current = data; }}
            onFleetOpenFormChange={(open) => { fleetOpenForm.current = open; }}
          />
        </div>
      </main>

      <ConfirmModal
        isOpen={skipConfirm !== null}
        title="Skip this step?"
        message={skipConfirm?.message ?? ''}
        confirmLabel="Skip for Now"
        onConfirm={confirmSkip}
        onClose={() => setSkipConfirm(null)}
      />

      {/* ── Footer — matches the app's nav bar (bg-plum) ─────────────── */}
      {!isDoneStep && (
        <footer className="fixed bottom-0 left-0 right-0 z-20 bg-plum px-4 py-3 safe-area-bottom">
          {stepError && (
            <div
              role="alert"
              className="max-w-2xl mx-auto mb-3 px-3.5 py-2.5 rounded-lg bg-white border border-rose/40 text-[13px] text-rose leading-snug"
            >
              {stepError}
            </div>
          )}
          <div className="max-w-2xl mx-auto flex gap-3">
            <button
              onClick={handleSkip}
              disabled={saving}
              className="flex-1 px-4 py-3 text-sm font-semibold text-white/70 hover:text-white
                         transition-colors rounded-xl"
            >
              Skip for Now
            </button>
            <button
              onClick={handleContinue}
              disabled={saving}
              className="flex-1 px-4 py-3 text-sm font-semibold text-plum bg-white
                         hover:bg-mist active:scale-95 disabled:opacity-50
                         rounded-xl transition-all duration-150 min-h-[44px]"
            >
              {saving ? 'Saving…' : 'Continue'}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}

// ── Step content router ────────────────────────────────────────────────────
// Each step will be implemented as a separate component in subsequent tickets.
// For now, render placeholder content.

interface StepContentProps {
  stepId: string;
  orgId: string;
  org: ReturnType<typeof useOrgSettings>['org'];
  checklist: ReturnType<typeof useSetupChecklist>['checklist'];
  visibleSteps: WizardStepConfig[];
  /** True after Continue was blocked on this step: steps highlight what's missing. */
  showErrors: boolean;
  onFinish: (destination: 'home' | 'new-quote') => Promise<void>;
  onCompanyProfileChange: (data: CompanyProfileData) => void;
  onMoveTypesChange: (data: MoveTypesData) => void;
  onResidentialSettingsChange: (data: ResidentialSettingsData) => void;
  onCommercialSettingsChange: (data: CommercialSettingsData) => void;
  onMaterialsPricingChange: (data: MaterialsPricingData) => void;
  onPaymentsChange: (data: PaymentsData) => void;
  onOnlineLeadCaptureChange: (data: OnlineLeadCaptureData) => void;
  onFleetOpenFormChange: (openForm: string | null) => void;
}

function SetupStepContent({ stepId, orgId, org, checklist, visibleSteps, showErrors, onFinish, onCompanyProfileChange, onMoveTypesChange, onResidentialSettingsChange, onCommercialSettingsChange, onMaterialsPricingChange, onPaymentsChange, onOnlineLeadCaptureChange, onFleetOpenFormChange }: StepContentProps) {
  switch (stepId) {
    case 'company-profile':
      return org ? <CompanyProfileStep org={org} onChange={onCompanyProfileChange} /> : null;
    case 'move-types':
      return org ? <MoveTypesStep org={org} onChange={onMoveTypesChange} /> : null;
    case 'fleet':
      return org ? <FleetStep org={org} orgId={orgId} onOpenFormChange={onFleetOpenFormChange} /> : null;
    case 'residential-settings':
      return org ? <ResidentialSettingsStep org={org} orgId={orgId} onChange={onResidentialSettingsChange} showErrors={showErrors} /> : null;
    case 'commercial-settings':
      return org ? <CommercialSettingsStep org={org} orgId={orgId} onChange={onCommercialSettingsChange} showErrors={showErrors} /> : null;
    case 'materials-pricing':
      return org ? <MaterialsPricingStep org={org} orgId={orgId} onChange={onMaterialsPricingChange} showErrors={showErrors} /> : null;
    case 'payments':
      return org ? <PaymentsStep org={org} onChange={onPaymentsChange} /> : null;
    case 'online-lead-capture':
      return org ? <OnlineLeadCaptureStep org={org} onChange={onOnlineLeadCaptureChange} /> : null;
    case 'invite-team':
      return org ? <InviteTeamStep org={org} orgId={orgId} /> : null;
    case 'done':
      return <DoneStep checklist={checklist} visibleSteps={visibleSteps} onFinish={onFinish} />;
    default:
      return (
        <div className="rounded-xl border border-stone bg-mist p-6 text-center">
          <p className="text-sm text-slate/70">
            This step will be implemented in a subsequent update.
          </p>
        </div>
      );
  }
}

function DoneStep({
  checklist,
  visibleSteps,
  onFinish,
}: {
  checklist: StepContentProps['checklist'];
  visibleSteps: WizardStepConfig[];
  onFinish: (destination: 'home' | 'new-quote') => Promise<void>;
}) {
  const navigate = useNavigate();
  const [finishing, setFinishing] = useState<'home' | 'new-quote' | null>(null);
  // Only sections the plan makes applicable count; completion comes from the
  // server (real org data), not from which steps were pressed through.
  const applicable = new Set(checklist?.applicableSections ?? []);
  const sections = visibleSteps.filter(
    (s): s is WizardStepConfig & { checklistKey: ChecklistSectionKey } =>
      s.id !== 'done' && !!s.checklistKey && applicable.has(s.checklistKey),
  );
  const remaining = sections.filter((s) => !checklist?.[s.checklistKey]);
  const allDone = remaining.length === 0;

  async function finish(destination: 'home' | 'new-quote') {
    setFinishing(destination);
    try { await onFinish(destination); } finally { setFinishing(null); }
  }

  return (
    <div className="text-center py-8">
      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${allDone ? 'bg-green-100' : 'bg-amber-100'}`}>
        <span className="text-3xl">{allDone ? '✓' : '…'}</span>
      </div>
      <h1 className="font-display text-h1 text-midnight mb-2">
        {allDone ? "You're All Set!" : 'Almost there'}
      </h1>
      <p className="text-small text-slate mb-8">
        {allDone
          ? 'Your organization is configured and ready to quote.'
          : `${remaining.length} of ${sections.length} required section${sections.length === 1 ? '' : 's'} still need${remaining.length === 1 ? 's' : ''} attention before quotes come out right.`}
      </p>

      {/* Checklist recap */}
      <div className="max-w-sm mx-auto text-left mb-8">
        {sections.map((step) => {
          const completed = !!checklist?.[step.checklistKey];
          const link = SECTION_LINKS[step.checklistKey];
          return (
            <div key={step.id} className="flex items-start gap-3 py-2.5 border-b border-stone last:border-0">
              <div className={`mt-0.5 w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-xs ${
                completed ? 'bg-green-500 text-white' : 'bg-amber-100 text-amber-800'
              }`}>
                {completed ? '✓' : '!'}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${completed ? 'text-midnight' : 'text-midnight font-medium'}`}>{step.title}</p>
                {!completed && (
                  <p className="text-xs text-slate mt-0.5">{SETUP_SECTION_REMAINING[step.checklistKey]}</p>
                )}
              </div>
              {!completed && (
                <button
                  type="button"
                  onClick={() => navigate(link.to)}
                  className="shrink-0 inline-flex items-center gap-1 min-h-[44px] px-2 text-xs font-semibold text-plum hover:text-rose transition-colors"
                >
                  {link.label}
                  <ArrowRight size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!allDone && (
        <p className="text-xs text-slate/70 mb-6">
          The setup banner at the top of the app will keep pointing here until these are done.
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {allDone && (
          <button
            type="button"
            onClick={() => finish('new-quote')}
            disabled={finishing !== null}
            className="btn-primary px-8 rounded-xl min-h-[44px] disabled:opacity-50"
          >
            {finishing === 'new-quote' ? 'Opening…' : 'Create your first quote'}
          </button>
        )}
        <button
          type="button"
          onClick={() => finish('home')}
          disabled={finishing !== null}
          className={`${allDone ? 'px-8 py-3 text-sm font-semibold text-plum bg-white border border-plum/30 hover:bg-mist rounded-xl' : 'btn-primary px-8 rounded-xl'} min-h-[44px] disabled:opacity-50`}
        >
          {finishing === 'home' ? 'Opening…' : allDone ? 'Go to home' : 'Go to home for now'}
        </button>
      </div>
    </div>
  );
}
